import React, { useEffect, useRef, useState } from "react";
import { Wrapper, WrapperState } from "../../Wrapper";
import { Button, Card, DatePicker, Form, Modal, PageHeader, Table, TablePaginationConfig, notification } from "antd";
import useAuth from "../../../hooks/useAuth";
import useFilesApi from "../../../hooks/backend/api/useFilesApi";
import usePackagesApi, { FieldsComposition, IPackageListItem, IScheduleListItem } from "../../../hooks/backend/api/usePackagesApi";
import IPagingConfig, { defaultPage, defaultPageSize, IPaging } from "../../../types/PagingConfig";
import saveFile from "../../../services/saveFile";
import { ScheduleListColumns } from "./ScheduleListColumns";
import { useScheduleListConfig } from "./ScheduuleListConfig";
import useBackendForm from "../../../hooks/useBackendForm";
import { Moment } from "moment";

interface IScheduleTableItem extends IScheduleListItem {
  key: string;
}

interface IScheduleListProps {
  fieldsComposition: FieldsComposition;
}

interface ICreationForm {
  date: Moment | undefined
}

export function ScheduleList(props: IScheduleListProps) {
  const unmounted = useRef(false);
  const packagesApi = usePackagesApi();
  const [isReferencesLoading, setIsReferencesLoading] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [initError, setInitError] = useState<any>(undefined);
  const [wrapperState, setWrapperState] = useState<WrapperState | null>(null);
  const [schedules, setSchedules] = useState<IScheduleListItem[]>([]);
  const { currentUser } = useAuth();
  const [totalRows, setTotalRows] = useState<number>(0);
  const [paging, setPaging] = useState<IPaging>({ page: defaultPage, pageSize: defaultPageSize })
  const [disablingSchedules, setDisablingSchedules] = useState<string[]>([]);
  const [enablingSchedules, setEnablingSchedules] = useState<string[]>([]);
  const [creation, setCreation] = useState(false);
  const creationForm = useBackendForm<ICreationForm>();
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    return () => {
      unmounted.current = true;
    };
  }, []);

  async function getSchedule() {
    try {
      setInitError(undefined);
      setLoading(true);

      const schedules = await packagesApi.getSchedule(
        props.fieldsComposition,
        paging.page,
        paging.pageSize);

      if (!unmounted.current) {
        const convertedSchedules: IScheduleTableItem[] = schedules.list.map(
          (r) => ({
            ...r,
            key: r.id,
          })
        );
        setTotalRows(schedules.totalRowCount);
        setSchedules(convertedSchedules);
      }
    } catch (error) {
      if (!unmounted.current) {
        setInitError(error);
      }
    } finally {
      if (!unmounted.current) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (currentUser?.canViewMonitoring()) {
      getSchedule();
    }
  }, [currentUser, props.fieldsComposition, paging]);

  useEffect(() => {
    if (currentUser === null) {
      setWrapperState('loading');
    } else if (!currentUser.canViewMonitoring()) {
      setWrapperState('accessDenied');
    } else if (initError) {
      setWrapperState('hasErrors');
    } else {
      setWrapperState(null);
    }
  }, [currentUser, initError]);

  function handleOnChange(pagination: TablePaginationConfig) {
    console.log('onChange');
    setPaging({ page: pagination.current ?? defaultPage, pageSize: pagination.pageSize ?? defaultPageSize });
  }

  async function handleOnDisable(id: string): Promise<void> {
    disablingSchedules.push(id);

    try {
      await packagesApi.disableSchedule(id);
      notification.success({ message: 'Дата в расписании отключена' })
    } catch (e: any) {
      notification.error({ message: 'Не удалось отключить дату в расписании' })
      return;
    } finally {
      const index = disablingSchedules.indexOf(id, 0);
      if (index > -1 && !unmounted.current) {
        disablingSchedules.splice(index, 1);
        setEnablingSchedules(disablingSchedules);
      }
    }

    await getSchedule()
  }

  async function handleOnEnable(id: string): Promise<void> {
    enablingSchedules.push(id);

    try {
      await packagesApi.enableSchedule(id);
    } catch (e: any) {
      notification.error({ message: 'Не удалось включить дату в расписании' })
      return;
    } finally {
      const index = enablingSchedules.indexOf(id, 0);
      if (index > -1 && !unmounted.current) {
        enablingSchedules.splice(index, 1);
        setEnablingSchedules(enablingSchedules);
      }
    }

    notification.success({ message: 'Дата в расписании включена' })
    await getSchedule()
  }

  async function handleOnCreate(values: ICreationForm) {
    if (values.date === undefined) return;
    
    setCreating(true);

    try {
      await packagesApi.createSchedule(props.fieldsComposition, values.date)
    } catch (e: any) {
      notification.error({ message: 'Не удалось создать дату в расписании' })
      return;
    } finally {
      if (!unmounted.current) {
        setCreating(false);
      }
    }

    notification.success({ message: 'Дата в расписании создана' })

    await getSchedule()

    setCreation(false);

    creationForm.form.resetFields();
  }

  function handleOnCancel() {
    setCreation(false);
    creationForm.form.resetFields();
  }

  function renderTable() {
    if (!currentUser) {
      return null;
    }
    return (
      <Table
        className="u-mt-4"
        columns={ScheduleListColumns({
          onDisableClick: handleOnDisable,
          onEnableClick: handleOnEnable,
          disablingSchedules,
          enablingSchedules
        })}
        loading={isLoading || isReferencesLoading}
        dataSource={schedules}
        scroll={{ x: 900 }}
        onChange={handleOnChange}
        pagination={{
          current: paging.page,
          position: ['bottomCenter'],
          pageSize: paging.pageSize,
          total: totalRows,
          showSizeChanger: false
        }}
      />
    );
  };

  return (
    <>
      <Wrapper state={wrapperState} error={initError}>
        <PageHeader
          title="Расписание постановок"
          extra={
            <Button
              onClick={() => setCreation(true)}
            >
              Добавить
            </Button>
          }
        />
        {renderTable()}
      </Wrapper>
      <Modal
        title='Добавление даты в расписание'
        visible={creation}
        closable={false}
        okText='Добавить'
        onOk={creationForm.form.submit}
        okButtonProps={{
          loading: creating
        }}
        cancelText='Отмена'
        onCancel={() => setCreation(false)}
        cancelButtonProps={{
          disabled: creating
        }}
      >
        <Form
          form={creationForm.form}
          onFinish={handleOnCreate}
        >
          <Form.Item name='date' label='Дата'>
            <DatePicker />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );

}